import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileText, X } from 'lucide-react'
import type { ChatReference } from '@jr2os/shared'

/**
 * Convierte texto plano con marcas [REF1], [REF2]… en nodos JSX donde cada
 * referencia es un chip clickable. Las refs desconocidas se pintan tal cual.
 */
export function renderWithCitations(
  text: string,
  references: ChatReference[],
  onPick: (ref: ChatReference) => void
): React.ReactNode[] {
  const refMap = new Map(references.map((r) => [r.ref, r]))
  const pattern = /\[(REF\d+)\]/g
  const out: React.ReactNode[] = []
  let lastIdx = 0
  let m: RegExpExecArray | null
  let key = 0
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIdx) {
      out.push(text.slice(lastIdx, m.index))
    }
    const refKey = m[1]!
    const ref = refMap.get(refKey)
    if (ref) {
      out.push(
        <button
          key={`ref-${key++}`}
          type="button"
          onClick={() => onPick(ref)}
          className="inline-flex items-center px-1.5 py-0.5 mx-0.5 text-[10px] font-semibold rounded bg-jr2-500/20 text-jr2-700 dark:text-jr2-200 border border-jr2-500/40 hover:bg-jr2-500/30 align-baseline"
          title={ref.documentName}
        >
          {refKey}
        </button>
      )
    } else {
      out.push(m[0])
    }
    lastIdx = m.index + m[0].length
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx))
  return out
}

/**
 * Panel lateral derecho que muestra el contenido del chunk citado +
 * enlace al documento. Cerrar con click fuera o botón X.
 */
export function CitationDrawer({
  open,
  reference,
  onClose,
}: {
  open: boolean
  reference: ChatReference | null
  onClose: () => void
}) {
  const { t } = useTranslation(['ai', 'common'])
  if (!open || !reference) return null
  return (
    <div className="fixed inset-0 z-40 pointer-events-none">
      <div
        className="absolute inset-0 bg-black/30 pointer-events-auto"
        onClick={onClose}
        aria-hidden
      />
      <aside className="absolute right-0 top-0 h-full w-[420px] surface-deep border-l border-base pointer-events-auto p-5 overflow-y-auto animate-slideUp">
        <div className="flex items-start justify-between gap-2 mb-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider t-faint">{reference.ref}</div>
            <h3 className="text-base font-semibold t-primary truncate flex items-center gap-2">
              <FileText size={16} className="text-jr2-700 dark:text-jr2-300 shrink-0" />
              {reference.documentName}
            </h3>
            {reference.pageStart && (
              <div className="text-xs t-muted mt-0.5">
                {t('citations.page')} {reference.pageStart}
                {reference.pageEnd && reference.pageEnd !== reference.pageStart
                  ? `-${reference.pageEnd}`
                  : ''}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-lg hover:bg-hover t-muted"
            aria-label={t('common:actions.close')}
          >
            <X size={16} />
          </button>
        </div>

        <div className="text-sm t-secondary leading-relaxed whitespace-pre-wrap">
          {reference.snippet}
        </div>

        <div className="mt-4 flex items-center gap-2">
          {reference.opportunityId && (
            <Link
              to={`/opportunities/${reference.opportunityId}`}
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
            >
              {t('citations.goToOpportunity')}
            </Link>
          )}
          <Link
            to={`/ocr?document=${reference.documentId}`}
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            {t('citations.viewFullText')}
          </Link>
        </div>
      </aside>
    </div>
  )
}

/** Estado compartido del drawer. Hook para no repetir useState en cada consumidor. */
export function useCitationDrawer(): {
  open: boolean
  ref: ChatReference | null
  show: (r: ChatReference) => void
  hide: () => void
} {
  const [ref, setRef] = useState<ChatReference | null>(null)
  return {
    open: ref !== null,
    ref,
    show: (r) => setRef(r),
    hide: () => setRef(null),
  }
}
